import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

export type AppFeatures = {
  tickets: boolean;
  leads: boolean;
  agreement: boolean;
  assessment: boolean;
  smartlombard: boolean;
  secret: boolean;
  wazzup: boolean;
  shift: boolean;
  payment_schedule: boolean;
  custom_dicts: boolean;
  strategies: boolean;
  import_data: boolean;
  ai_agent: boolean;
  testing: boolean;
  integration: boolean;
  sms: boolean;
  email: boolean;
  cpa: boolean;
  notifications: boolean;
  self_qr: boolean;
  task: boolean;
  entity: boolean;
  // AB3 Academy Phase 2: feature-gating для learning_service и sandbox_service.
  // Backend отдаёт оба независимо — learning без sandbox = только video/text/quiz,
  // sandbox без learning = админ-only debug. Обычно идут парой.
  learning: boolean;
  sandbox: boolean;
  // WABA (официальный WhatsApp Business через Meta Cloud API). Без флага
  // RequireFeature name="waba" → false и модуль скрыт. Backend отдаёт
  // app_features.waba=true когда у org есть хоть один настроенный канал.
  waba: boolean;
  // Telegram-канал (личный аккаунт через MTProto). Backend отдаёт
  // app_features.telegram=true когда фича включена для организации.
  telegram: boolean;
  // Instagram-канал (Meta). Backend отдаёт app_features.instagram=true
  // когда фича включена для организации. Алиасов нет — только точный ключ.
  instagram: boolean;
  // TikTok-канал (TikTok Direct). Backend отдаёт app_features.tiktok=true
  // когда фича включена для организации. Алиасов нет — только точный ключ.
  tiktok: boolean;
};

const FEATURE_KEYS: readonly (keyof AppFeatures)[] = [
  'tickets', 'leads', 'agreement', 'assessment', 'smartlombard',
  'secret', 'wazzup', 'shift', 'payment_schedule',
  'custom_dicts', 'strategies', 'import_data', 'ai_agent', 'testing',
  'integration', 'sms', 'email', 'cpa', 'notifications', 'self_qr', 'task', 'entity',
  'learning', 'sandbox',
  'waba',
  'telegram',
  'instagram',
  'tiktok',
];

const ALL_DISABLED = Object.fromEntries(
  FEATURE_KEYS.map(k => [k, false]),
) as AppFeatures;

const Ctx = createContext<AppFeatures | null>(null);

// Алиасы: бэкенд иногда отдаёт ключи в единственном числе или с другим написанием,
// но семантически это та же фича.
const FEATURE_ALIASES: Partial<Record<keyof AppFeatures, readonly string[]>> = {
  notifications: ['notification'],
  self_qr: ['selfQr', 'self-qr', 'selfqr'],
  task: ['tasks', 'workflow', 'workflowkit'],
  entity: ['entities', 'entity_engine', 'entityEngine'],
  waba: ['WABA', 'whatsapp_business', 'whatsapp_official'],
  telegram: ['telegram_user', 'telegram_channel', 'tg'],
};

// Бэкенд присылает только включённые фичи; отсутствие ключа = выключено.
// Экспортируется для unit-тестов парсинга (см. AppFeaturesContext.test.tsx).
export const featuresFromProfile = (profile: any | null | undefined): AppFeatures => {
  if (!profile) return ALL_DISABLED;
  const af = profile.app_features ?? {};
  return Object.fromEntries(
    FEATURE_KEYS.map(k => {
      if (af[k] === true) return [k, true];
      const aliases = FEATURE_ALIASES[k];
      if (aliases && aliases.some(a => af[a] === true)) return [k, true];
      return [k, false];
    }),
  ) as AppFeatures;
};

export const AppFeaturesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { profile, tokens } = useAuth();
  const value = useMemo<AppFeatures>(
    () => (tokens && !profile) ? ALL_DISABLED : featuresFromProfile(profile),
    [profile, tokens],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAppFeatures = (): AppFeatures => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppFeatures must be used within AppFeaturesProvider');
  return v;
};
